/**
 * TFSA contribution room calculator (CRA limits 2009–2026).
 */

import type { TfsaInput, TfsaResult, TfsaYearlyBreakdown } from '../shared/types';

export const TFSA_ANNUAL_LIMITS: Record<number, number> = {
  2009: 5000,
  2010: 5000,
  2011: 5000,
  2012: 5000,
  2013: 5500,
  2014: 5500,
  2015: 10000,
  2016: 5500,
  2017: 5500,
  2018: 5500,
  2019: 6000,
  2020: 6000,
  2021: 6000,
  2022: 6000,
  2023: 6500,
  2024: 7000,
  2025: 7000,
  2026: 7000,
};

const TFSA_START_YEAR = 2009;
const MIN_ELIGIBLE_AGE = 18;

export function getAnnualLimit(year: number): number | null {
  return TFSA_ANNUAL_LIMITS[year] ?? null;
}

export function getEligibilityStartYear(birthYear: number): number {
  return Math.max(TFSA_START_YEAR, birthYear + MIN_ELIGIBLE_AGE);
}

export function calculateTfsaRoom({
  year,
  age,
  priorContributions,
  canadianResident = true,
}: TfsaInput): TfsaResult {
  if (!TFSA_ANNUAL_LIMITS[year]) {
    return { error: `TFSA limits are only available for ${TFSA_START_YEAR}–2026.` };
  }

  if (age < MIN_ELIGIBLE_AGE) {
    return {
      error: 'You must be 18 or older to accumulate TFSA contribution room.',
      eligible: false,
    };
  }

  if (!canadianResident) {
    return {
      error: 'You must be a Canadian resident to accumulate TFSA contribution room.',
      eligible: false,
    };
  }

  const birthYear = year - age;
  const startYear = getEligibilityStartYear(birthYear);
  const contributions = Math.max(0, priorContributions);

  const yearlyBreakdown: TfsaYearlyBreakdown[] = [];
  let cumulativeRoom = 0;

  for (let y = TFSA_START_YEAR; y <= year; y++) {
    const annualLimit = TFSA_ANNUAL_LIMITS[y] ?? 0;
    const eligible = canadianResident && y >= startYear;
    const roomAdded = eligible ? annualLimit : 0;
    cumulativeRoom += roomAdded;

    yearlyBreakdown.push({
      year: y,
      annualLimit,
      roomAdded,
      cumulativeRoom,
      eligible,
    });
  }

  const remainingRoom = cumulativeRoom - contributions;

  return {
    year,
    age,
    birthYear,
    eligibilityStartYear: startYear,
    priorContributions: contributions,
    cumulativeRoom,
    remainingRoom,
    yearlyBreakdown,
    eligible: true,
  };
}
